import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { TICKET_PRICE } from '../../../core/constants/lottery';
import { MAX_CART_SIZE } from '../../../core/constants/store';

// TSS vite/03 §"Form state" — a per-flow context: the selection being built
// before checkout. Mounted by the store layout, disposed with it.

const CartContext = createContext(null);

export function CartProvider({ children }) {
    const [selected, setSelected] = useState([]);

    const selectedSet = useMemo(() => new Set(selected), [selected]);

    const add = useCallback((numbers) => {
        const incoming = Array.isArray(numbers) ? numbers : [numbers];
        let rejected = 0;

        setSelected((current) => {
            const next = [...current];
            const seen = new Set(current);
            for (const number of incoming) {
                if (seen.has(number)) continue;
                if (next.length >= MAX_CART_SIZE) { rejected += 1; continue; }
                seen.add(number);
                next.push(number);
            }
            return next.length === current.length ? current : next;
        });

        if (rejected > 0) {
            toast.error(`Puedes llevar máximo ${MAX_CART_SIZE} números por compra`);
        }
    }, []);

    const remove = useCallback((number) => {
        setSelected(current => current.filter(item => item !== number));
    }, []);

    const toggle = useCallback((number) => {
        if (selectedSet.has(number)) remove(number);
        else add(number);
    }, [selectedSet, add, remove]);

    const clear = useCallback(() => setSelected([]), []);

    const value = useMemo(() => ({
        selected,
        isSelected: (number) => selectedSet.has(number),
        count: selected.length,
        total: selected.length * TICKET_PRICE,
        isFull: selected.length >= MAX_CART_SIZE,
        add,
        remove,
        toggle,
        clear,
    }), [selected, selectedSet, add, remove, toggle, clear]);

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
    return ctx;
}
